import { isDefined } from "@clipboard-health/util-ts";
import { Box, CircularProgress, Stack, Tooltip } from "@mui/material";
import { I18N_NAMESPACES } from "@src/appV2/i18n/constants";
import { useTu } from "@src/appV2/i18n/useTu";
import { formatAgentRequestLabel } from "@src/appV2/redesign/DailyView/utils/formatAgentRequestLabel";
import { useState } from "react";

import { Checkbox } from "../../../components/Checkbox";
import { IconButton } from "../../../components/IconButton";
import { Text } from "../../../components/Text";

export interface WorkerTypeItem {
  hasActiveShifts: boolean;
  isWithoutCharge: boolean;
  name: string;
  visible: boolean;
}

interface WorkerTypeRowItemProps {
  hasActiveShifts: boolean;
  index: number;
  isAdmin: boolean;
  isLoadingShiftCounts: boolean;
  isUpdating: boolean;
  totalCount: number;
  workerType: WorkerTypeItem;
  onMoveDown: (index: number) => void;
  onMoveUp: (index: number) => void;
  onVisibilityToggle: (index: number, checked: boolean) => void;
  disabledTooltip?: string;
}

export function WorkerTypeRowItem({
  hasActiveShifts,
  index,
  isAdmin,
  isLoadingShiftCounts,
  isUpdating,
  totalCount,
  workerType,
  onMoveDown,
  onMoveUp,
  onVisibilityToggle,
  disabledTooltip,
}: WorkerTypeRowItemProps) {
  const { tu } = useTu(I18N_NAMESPACES.settings);
  const label = formatAgentRequestLabel(workerType.name);
  const isLockedByActiveShifts = workerType.visible && hasActiveShifts;
  const isCheckboxDisabled =
    !isAdmin || isLockedByActiveShifts || isUpdating || workerType.isWithoutCharge;
  const isReorderDisabled = !isAdmin || isUpdating || workerType.isWithoutCharge;

  const [anchorElement, setAnchorElement] = useState<HTMLSpanElement | null>(null);

  return (
    <Tooltip
      arrow
      title={disabledTooltip ?? ""}
      placement="right"
      PopperProps={{
        ...(isDefined(anchorElement) && { anchorEl: anchorElement }),
      }}
    >
      <Stack
        alignItems="flex-start"
        direction="row"
        spacing={3}
        sx={(theme) => ({ padding: `${theme.spacing(1)} 0` })}
      >
        <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0, opacity: isAdmin ? 1 : 0.4 }}>
          <IconButton
            aria-label={tu("Move {{label}} up", { values: { label } })}
            disabled={isReorderDisabled || index === 0}
            iconType="chevron-up"
            size="small"
            variant="muted"
            onClick={() => {
              onMoveUp(index);
            }}
          />
          <IconButton
            aria-label={tu("Move {{label}} down", { values: { label } })}
            disabled={isReorderDisabled || index === totalCount - 1}
            iconType="chevron-down"
            size="small"
            variant="muted"
            onClick={() => {
              onMoveDown(index);
            }}
          />
        </Stack>

        <Box sx={{ display: "flex", alignItems: "flex-start", flex: 1, gap: 2, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", opacity: isAdmin ? 1 : 0.4 }}>
            <Checkbox
              ariaLabel={label}
              checked={workerType.visible}
              disabled={isCheckboxDisabled}
              onChange={(checked) => {
                onVisibilityToggle(index, checked);
              }}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box ref={setAnchorElement} component="span">
              <Text
                inline
                sx={(theme) => ({
                  fontWeight: theme.typography.fontWeightMedium,
                  color:
                    !isAdmin && !workerType.visible
                      ? theme.palette.text.disabled
                      : theme.palette.text.primary,
                })}
                variant="body2"
              >
                {label}
              </Text>
            </Box>
            {isAdmin && isLockedByActiveShifts && (
              <Text
                inline
                sx={(theme) => ({
                  marginLeft: theme.spacing(2),
                  color: theme.palette.text.secondary,
                })}
                variant="caption"
              >
                {tu("Has active shifts in the next 2 weeks")}
              </Text>
            )}
          </Box>
          {isAdmin && isLoadingShiftCounts && workerType.visible && (
            <CircularProgress size={14} sx={{ flexShrink: 0, marginTop: 0.5 }} />
          )}
        </Box>
      </Stack>
    </Tooltip>
  );
}
